<template>
  <div class="d-flex align-center ga-2">
    <h3>Asset Lifecycle Costs</h3>
    <DateField
      class="width-160"
      v-model="start"
      label="From"
      variant="outlined"
      density="compact"
      hide-details
      @change="getData"
    />
    <DateField
      class="width-160"
      v-model="end"
      label="To"
      variant="outlined"
      density="compact"
      hide-details
      @change="getData"
    />
    <v-autocomplete
      class="width-160 compact-autocomplete"
      v-model="ampMasterIds"
      label="AMP"
      :items="ampMasters"
      item-value="id"
      item-title="name"
      multiple
      variant="outlined"
      density="compact"
      width="400px"
      max-width="400px"
      hide-details
      @update:model-value="getData"
    />
    <FLOCSearch
      v-model="flocs"
      label="FLOC"
      variant="outlined"
      density="compact"
      width="400px"
      max-width="400px"
      hide-details
      multiple
      @update:model-value="getData"
    />
    <v-autocomplete
      class="width-160 compact-autocomplete"
      v-model="orderTypes"
      label="Order Type"
      :items="orderTypesList"
      multiple
      variant="outlined"
      density="compact"
      width="400px"
      max-width="400px"
      single-line
      hide-details
      @update:model-value="getData"
    />
    <v-autocomplete
      class="width-160 compact-autocomplete"
      v-model="tacticsSelected"
      label="Tactic"
      :items="tactics"
      item-value="id"
      item-title="task_list_header_description"
      multiple
      variant="outlined"
      density="compact"
      width="400px"
      max-width="400px"
      single-line
      hide-details
      @update:model-value="getData"
    />
    <v-radio-group v-model="aggregate" inline hide-details>
      <v-radio
        v-for="aggregate in costsAggregates"
        :key="aggregate"
        :label="aggregate"
        :value="aggregate"
      />
    </v-radio-group>
    <v-spacer />
    <v-progress-circular v-if="fetchingCount > 0" indeterminate :size="25" />
  </div>

  <div class="mt-2" style="height: 600px">
    <Line :data="data" :options="options" />
  </div>
</template>

<script setup lang="ts">
import {
  AssetManagementPlanMaster,
  DateObject,
  FunctionalLocation,
  Tactic,
} from "@/classes";
import DateField from "@/components/base/inputs/DateField.vue";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartData,
  ChartOptions,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { computed, onMounted, ref } from "vue";
import { Line } from "vue-chartjs";
import NuxtAxiosInstance from "@/plugins/axios";
import Decimal from "decimal.js";
import { useListStore } from "@/store/lists";
import { format } from "date-fns";
import FLOCSearch from "@/components/asset-management-plan/FLOCSearch.vue";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const $axios = NuxtAxiosInstance;

const lists = useListStore();
const ampMasters = lists.useList<AssetManagementPlanMaster>(
  "assetManagementPlanMasters"
);

const orderTypesList = ref(["PM01", "PM02", "PM03"]);
const orderTypes = ref<string[]>([]);
const tacticsSelected = ref<string[]>([]);

const costsAggregates = ["Cumulative", "Sum"] as const;
type CostsAggregate = (typeof costsAggregates)[number];
type AssetLifecycleCosts = {
  workorders: Record<string, Decimal>;
  tactics: Record<string, Decimal>;
};

const start = ref<DateObject | null>(
  DateObject.add(new DateObject(), { years: -1 })
);
const end = ref<DateObject | null>(new DateObject());
const ampMasterIds = ref<string[]>([]);
const flocs = ref<FunctionalLocation[]>([]);
const aggregate = ref<CostsAggregate>("Cumulative");

const costs = ref<AssetLifecycleCosts>({
  workorders: {},
  tactics: {},
});

const tactics = ref<Tactic[]>([]);
const abortController = ref(new AbortController());
const fetchingCount = ref(0);

onMounted(() => getData());

const getData = async () => {
  try {
    abortController.value.abort();
    abortController.value = new AbortController();
    fetchingCount.value++;
    costs.value = await $axios.$post<AssetLifecycleCosts>(
      "/metrics/asset-lifecycle-costs",
      {
        start: start.value,
        end: end.value,
        amp_master_ids: ampMasterIds.value,
        floc_ids: flocs.value.map((floc) => floc.id),
        order_types: orderTypes.value,
        tactics: tacticsSelected.value,
      },
      {
        signal: abortController.value.signal,
      }
    );

    tactics.value = await $axios.$post<Tactic[]>(
      "/metrics/asset-lifecycle-costs/plans",
      {
        start: start.value,
        end: end.value,
        amp_master_ids: ampMasterIds.value,
        floc_ids: flocs.value.map((floc) => floc.id),
        order_types: orderTypes.value,
        tactics: [],
      },
      {
        signal: abortController.value.signal,
      }
    );
  } catch (e) {
    // Aborted
  } finally {
    fetchingCount.value--;
  }
};

const data = computed<ChartData<"line">>(() => {
  const dateSets = new Set<string>();
  Object.keys(costs.value.workorders).forEach((date) => {
    dateSets.add(date);
  });
  Object.keys(costs.value.tactics).forEach((date) => {
    dateSets.add(date);
  });

  const dates = Array.from(dateSets).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );
  const workorderCosts: number[] = [];
  const tacticCosts: number[] = [];
  let workorderSum = 0;
  let tacticSum = 0;
  for (const date of dates) {
    const workorderCost = costs.value.workorders[date] ?? new Decimal(0);
    const tacticCost = costs.value.tactics[date] ?? new Decimal(0);
    if (aggregate.value === "Cumulative") {
      workorderSum += workorderCost.toNumber();
      tacticSum += tacticCost.toNumber();
      workorderCosts.push(workorderSum);
      tacticCosts.push(tacticSum);
    } else {
      workorderCosts.push(workorderCost.toNumber());
      tacticCosts.push(tacticCost.toNumber());
    }
  }

  return {
    labels: dates,
    datasets: [
      {
        label: "Actual",
        data: workorderCosts,
        borderColor: "rgb(75, 192, 192)",
        fill: true,
        backgroundColor: "rgb(75, 192, 192, 0.2)",
        tension: 0,
      },
      {
        label: "Forecast",
        data: tacticCosts,
        borderColor: "rgb(245, 142, 47)",
        fill: true,
        backgroundColor: "rgb(245, 142, 47, 0.2)",
        tension: 0,
      },
    ],
  };
});

const options = computed<ChartOptions<"line">>(() => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time",
        time: {
          unit: "month",
        },
      },
      y: {
        min: 0,
        ticks: {
          callback: (value) => {
            return `$${value.toLocaleString()}`;
          },
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: (items) => {
            const date = new Date(items[0].parsed.x);
            return format(date, "dd MMM yyyy");
          },
          label: (item) => {
            return `${item.dataset.label}: $${item.parsed.y.toLocaleString()}`;
          },
        },
      },
    },
  };
});
</script>

<style scoped>
.compact-autocomplete :deep(.v-field__field) {
  overflow-y: auto;
  height: 40px;
}
</style>
